import { useMemo } from "react";
import * as Yup from "yup";
import { useReactiveVar } from "@apollo/client";
import { useTranslation } from "react-i18next";

import { currentCompanyIdVar } from "@me-team/host/main/globalVar/state";
import { menuLanguageVersionVar } from "@me-team/host/main/menuLanguageVersions/state";
import { useMenuLanguagesData } from "../../Me-menu/utils/hooks/useMenuLanguagesData";
import { buildTranslationTextVm } from "../../Me-menu/utils/translationVersions/buildTranslationTextVm";
import { useGetAllMenuCategoriesForEditPageQuery } from "@me-team/host/main/editMenuPage/graphql/editMenuPage.hooks";
import { CloneDishesInterface } from "../interfaces/interface";

export type CloneDishFormValues = {
    dishName: string;
    currentCategoryId: number | null;
    currentSubcategoryId: number | null;
};

type UseCloneDishModalBaseDataProps = {
    isOpen: boolean;
    menuId: number;
    cloneDishModalInfo: CloneDishesInterface | null;
};

export const collapseSpaces = (value: string): string => value.replace(/\s{2,}/g, " ");

export const normalizeDishName = (value: string): string => collapseSpaces(value).trim();

export const useCloneDishModalBaseData = ({ isOpen, menuId, cloneDishModalInfo }: UseCloneDishModalBaseDataProps) => {
    const { t: translate } = useTranslation();

    const currentCompanyId = useReactiveVar(currentCompanyIdVar);
    const selectedLangVersionCode = useReactiveVar(menuLanguageVersionVar);

    const { primaryLanguage } = useMenuLanguagesData(Number(currentCompanyId));
    const primaryLanguageIso = primaryLanguage?.isoCode?.trim() || null;

    const isTranslationMode = Boolean(
        selectedLangVersionCode && primaryLanguageIso && selectedLangVersionCode !== primaryLanguageIso
    );

    const translationTooltip = translate("Edit the language version");

    const dishNameVm = useMemo(() => {
        return buildTranslationTextVm({
            translatedValue: cloneDishModalInfo?.cloneItem.name,
            originValue: cloneDishModalInfo?.cloneItem?.originalName,
            isTranslationMode,
        });
    }, [cloneDishModalInfo?.cloneItem?.name, cloneDishModalInfo?.cloneItem?.originalName, isTranslationMode]);

    const { data, loading, error } = useGetAllMenuCategoriesForEditPageQuery({
        skip: !isOpen || !cloneDishModalInfo || !currentCompanyId || !selectedLangVersionCode,
        variables: {
            companyId: Number(currentCompanyId),
            locale: selectedLangVersionCode,
            menuId: Number(menuId),
            keepWithoutMenuRelation: true,
        },
        fetchPolicy: "network-only",
    });

    const initialValues: CloneDishFormValues = useMemo(() => {
        return {
            dishName: `${translate("Copy -")} ${dishNameVm.displayText || ""}`,
            currentCategoryId: cloneDishModalInfo?.categoryInfo.categoryId,
            currentSubcategoryId: cloneDishModalInfo?.categoryInfo.subcategoryId,
        };
    }, [
        dishNameVm.displayText,
        cloneDishModalInfo?.categoryInfo.categoryId,
        cloneDishModalInfo?.categoryInfo.subcategoryId,
        translate,
    ]);

    const validationSchema = useMemo(() => {
        return Yup.object({
            dishName: Yup.string()
                .transform((value) => normalizeDishName(value || ""))
                .required(translate("Dish name is required"))
                .max(50, translate("Maximum 50 characters")),
            currentCategoryId: Yup.number().nullable().required(translate("Category is required")),
            currentSubcategoryId: Yup.number().nullable(),
        });
    }, [translate]);

    const categories = data?.user?.company?.meMenuCategories || null;

    return {
        translate,
        loading,
        error,
        isTranslationMode,
        translationTooltip,
        dishNameVm,
        initialValues,
        validationSchema,
        categories,
        collapseSpaces,
        normalizeDishName,
        primaryLanguage,
        selectedLangVersionCode,
    };
};
